/* ac-dilute.js — the dilution calculator ("Mix & Buy") used by sections/ac-dilute.liquid and
   snippets/ac-dilute-embed.liquid.

   Pure core first, exposed as window.__acDilute and tested in scripts/__tests__/ac-dilute.test.js.
   The DOM layer at the bottom runs only when there is a document.

   How each chemical is diluted depends on how it is sold (the "basis"):
   - ww: acids, bases, peroxide, bleach, salt solutions and glycerin are % by weight. The finished batch weighs
     V x rho(target); the concentrate supplies C2/C1 of that mass. rho() comes from DENSITY below.
   - vv: alcohols, ketones and glycols are mixed by volume (C1V1 = C2V2) and topped up to volume.
   - solid: powders and crystals are weighed, % w/v = grams per 100 mL of finished solution.
   Ratio 1:X is always 1 part concentrate + X parts water by volume; the result reports the real strength.
   ppm (hypochlorites) is mg/L of available chlorine. */
(function () {
  "use strict";

  var GAL_L = 3.785411784;
  var QT_L = GAL_L / 4;
  var FLOZ_L = GAL_L / 128;
  var TSP_ML = 4.92892159375;
  var TBSP_ML = TSP_ML * 3;
  var LB_G = 453.59237;
  var OZ_G = 28.349523125;
  var WATER = 0.9982; /* g/mL at 20 C */
  var NAOCL_TO_CL2 = 0.9525; /* available chlorine per unit NaOCl, 70.906 / 74.442 */

  /* g/mL at 20 C by wt%. Laliberte 2009 (J. Chem. Eng. Data 54:1725) via thermo, to each species' validity limit;
     H2SO4 above 78.2% from the CRC Handbook; glycerin from Volk & Kahler 2018 (Exp. Fluids 59:75), fit to Bosart &
     Snoddy 1928. Regenerate: python scripts/ac-dilute/build-densities.py */
  // prettier-ignore
  var DENSITY = {"AcOH":[[0,0.9982],[0.5,0.9989],[1,0.9996],[2,1.0009],[3,1.0023],[4,1.0036],[5,1.005],[6,1.0063],[8,1.009],[10,1.0116],[12,1.0143],[15,1.0182],[20,1.0246],[25,1.0308],[30,1.0368],[35,1.0425],[40,1.0479],[45,1.053],[50,1.0575],[55,1.0616],[60,1.065],[65,1.0677],[70,1.0696],[75,1.0704],[80,1.0699],[85,1.0681],[90,1.0645],[95,1.0588],[100,1.0506]],"HCOOH":[[0,0.9982],[0.5,0.9994],[1,1.0007],[2,1.0032],[3,1.0056],[4,1.0081],[5,1.0106],[6,1.013],[8,1.0179],[10,1.0228],[12,1.0277],[15,1.035],[20,1.0472],[25,1.0592],[30,1.071],[35,1.0828],[40,1.0945],[45,1.106],[50,1.1173],[55,1.1285],[60,1.1396],[65,1.1505],[70,1.1611],[75,1.1717],[80,1.182],[85,1.1921],[90,1.202],[95,1.2117],[100,1.2211]],"FeCl3":[[0,0.9982],[0.5,1.0023],[1,1.0064],[2,1.0147],[3,1.0231],[4,1.0316],[5,1.0402],[6,1.0489],[8,1.0666],[10,1.0848],[12,1.1034],[15,1.1321],[20,1.1824],[25,1.2358],[30,1.2925],[35,1.3525],[40,1.4162],[45,1.4834],[50,1.5543]],"H2O2":[[0,0.9982],[0.5,1.0],[1,1.0019],[2,1.0056],[3,1.0093],[4,1.013],[5,1.0167],[6,1.0204],[8,1.0278],[10,1.0353],[12,1.0428],[15,1.0542],[20,1.0733],[25,1.0928],[30,1.1126],[35,1.1328],[40,1.1534],[45,1.1744],[50,1.196],[55,1.218],[60,1.2406],[65,1.2638],[70,1.2876],[75,1.3121],[80,1.3374],[85,1.3634],[90,1.3902],[95,1.4178],[99.61,1.4442]],"H3PO4":[[0,0.9982],[0.5,1.0009],[1,1.0035],[2,1.0089],[3,1.0143],[4,1.0198],[5,1.0253],[6,1.0309],[8,1.0422],[10,1.0537],[12,1.0655],[15,1.0835],[20,1.1146],[25,1.1472],[30,1.1815],[35,1.2174],[40,1.255],[45,1.2945],[50,1.336],[55,1.3795],[60,1.4252],[65,1.4732],[70,1.5234],[75,1.5761],[80,1.6313],[85,1.6891]],"H2SO4":[[0,0.9982],[0.5,1.0015],[1,1.0049],[2,1.0116],[3,1.0184],[4,1.0252],[5,1.032],[6,1.0389],[8,1.0528],[10,1.0669],[12,1.0812],[15,1.103],[20,1.1404],[25,1.1791],[30,1.2192],[35,1.2609],[40,1.3043],[45,1.3495],[50,1.3967],[55,1.446],[60,1.4976],[65,1.5516],[70,1.6084],[75,1.6681],[78.2,1.708],[80,1.7272],[82,1.7491],[84,1.7693],[86,1.7872],[88,1.8022],[90,1.8144],[92,1.824],[94,1.8312],[96,1.8355],[98,1.8361]],"HCl":[[0,0.9982],[0.5,1.0006],[1,1.0031],[2,1.0079],[3,1.0128],[4,1.0177],[5,1.0226],[6,1.0275],[8,1.0374],[10,1.0473],[12,1.0572],[15,1.0723],[20,1.0975],[25,1.1229],[30,1.1486],[35,1.1743],[37.57,1.1875]],"HNO3":[[0,0.9982],[0.5,1.001],[1,1.0037],[2,1.0093],[3,1.0149],[4,1.0206],[5,1.0263],[6,1.0321],[8,1.0437],[10,1.0554],[12,1.0673],[15,1.0855],[20,1.1164],[25,1.1481],[30,1.1805],[35,1.2132],[40,1.246],[45,1.2784],[50,1.31],[55,1.3399],[60,1.3673],[65,1.3911],[70,1.4099]],"KOH":[[0,0.9982],[0.5,1.0027],[1,1.0072],[2,1.0163],[3,1.0254],[4,1.0346],[5,1.0438],[6,1.053],[8,1.0717],[10,1.0904],[12,1.1094],[15,1.1382],[20,1.1872],[25,1.2375],[30,1.2892],[35,1.3424],[40,1.3972],[45,1.4538],[50,1.5123],[51.86,1.5346]],"NaOH":[[0,0.9982],[0.5,1.0037],[1,1.0092],[2,1.0202],[3,1.0312],[4,1.0422],[5,1.0532],[6,1.0643],[8,1.0864],[10,1.1085],[12,1.1307],[15,1.1638],[20,1.2187],[25,1.2728],[30,1.3257],[35,1.3769],[40,1.426],[45,1.4726],[50,1.5163]],"NH3":[[0,0.9982],[0.5,0.9961],[1,0.9941],[2,0.99],[3,0.9859],[4,0.9819],[5,0.978],[6,0.9741],[8,0.9664],[10,0.9588],[12,0.9514],[15,0.9406],[20,0.9232],[25,0.9065],[30,0.8906],[35,0.8754],[40,0.8607],[45,0.8466],[50,0.833],[52.19,0.8273]],"Glycerin":[[0,0.998],[0.5,0.9992],[1,1.0003],[2,1.0027],[3,1.005],[4,1.0074],[5,1.0097],[6,1.0121],[8,1.0169],[10,1.0218],[12,1.0267],[15,1.0341],[20,1.0467],[25,1.0595],[30,1.0725],[35,1.0857],[40,1.0991],[45,1.1125],[50,1.1261],[55,1.1397],[60,1.1534],[65,1.1671],[70,1.1807],[75,1.1943],[80,1.2079],[85,1.2214],[90,1.2349],[95,1.2482],[100,1.2608]]};

  /* Strengths Alliance sells ready to use, by family: target % -> product handle. */
  // prettier-ignore
  var READY = {
    H2O2: { 3: "hydrogen-peroxide-3-technical-grade", 5: "hydrogen-peroxide-5-technical-grade", 6: "hydrogen-peroxide-6-technical-grade", 10: "hydrogen-peroxide-10-technical-grade", 12: "hydrogen-peroxide-12-technical-grade", 15: "hydrogen-peroxide-15-technical-grade", 25: "hydrogen-peroxide-25-technical-grade", 30: "hydrogen-peroxide-30-technical" },
    NaOH: { 5: "sodium-hydroxide-5-technical-grade", 10: "sodium-hydroxide-10-technical-grade", 15: "sodium-hydroxide-15-technical-grade", 20: "sodium-hydroxide-20-technical-grade", 25: "sodium-hydroxide-25", 30: "sodium-hydroxide-30-technical-grade", 50: "sodium-hydroxide-50-membrane-grade-caustic-soda-lye" },
    KOH: { 25: "potassium-hydroxide-25-solution-technical-grade-caustic-potash-liquid-koh", 30: "potassium-hydroxide-30-solution-technical-grade", 45: "potassium-hydroxide-45-solution-technical-grade-caustic-potash-liquid-koh" },
    NH3: { 5: "ammonium-hydroxide-5-technical-grade", 10: "ammonium-hydroxide-10-technical-grade", 15: "ammonium-hydroxide-15-technical-grade", 20: "ammonium-hydroxide-20-technical-grade", 29: "ammonium-hydroxide-29-technical-grade" },
    HCl: { 5: "hydrochloric-acid-5-technical-grade", 10: "hydrochloric-acid-10-technical-grade", 15: "hydrochloric-acid-15-technical-grade", 20: "hydrochloric-acid-20-technical-grade", 25: "hydrochloric-acid-25-technical-grade", 31: "hydrochloric-acid-31-technical-grade", 37: "hydrochloric-acid-37-hcl-37-technical-grade" },
    H2SO4: { 30: "sulfuric-acid-30", 37: "sulfuric-acid-37-battery-acid", 50: "sulfuric-acid-50-electrolyte-grade", 70: "sulfuric-acid-70", 93: "sulfuric-acid-93-technical-grade" },
    HNO3: { 5: "nitric-acid-5", 20: "nitric-acid-20", 25: "nitric-acid-25", 40: "nitric-acid-40", 65: "nitric-acid-65-technical-grade", 70: "nitric-acid-70-technical-grade" },
    H3PO4: { 5: "phosphoric-acid-5-technical-grade", 10: "phosphoric-acid-10-technical-grade", 15: "phosphoric-acid-15-technical-grade", 20: "phosphoric-acid-20-technical-grade", 30: "phosphoric-acid-30-the-ultimate-rust-remover-solution", 45: "phosphoric-acid-45-technical-grade", 50: "phosphoric-acid-50-technical-grade", 75: "phosphoric-acid-75", 85: "phosphoric-acid-85-technical-grade" },
    NaOCl: { 1: "sodium-hypochlorite-1", 2: "sodium-hypochlorite-2", 3: "sodium-hypochlorite-3", 5.25: "sodium-hypochlorite-5-25", 6: "sodium-hypochlorite-6", 8.25: "sodium-hypochlorite-8-25", 10: "sodium-hypochlorite-10", 12.5: "sodium-hypochlorite-12-5" },
    AcOH: { 5: "5-acetic-acid-food-grade", 10: "10-vinegar-concentrated-industrial-strength", 20: "20-acetic-acid-food-grade", 30: "30-vinegar-concentrated-industrial-strength", 50: "50-vinegar-concentrated-industrial-strength", 75: "75-vinegar" },
    FeCl3: { 10: "ferric-chloride-10", 20: "ferric-chloride-20", 30: "ferric-chloride-30", 40: "ferric-chloride-40" },
    Citric: { 25: "citric-acid-25-solution", 50: "citric-acid-50-solution" },
    IPA: { 50: "isopropyl-alcohol-50-technical-grade", 60: "isopropyl-alcohol-60-technical-grade", 70: "isopropyl-alcohol-70-usp-grade", 91: "isopropyl-alcohol-91", 99: "isopropyl-alcohol-99" },
    Glycerin: { 25: "glycerin-25-food-grade", 50: "glycerin-50-food-grade", 75: "glycerin-75-food-grade" },
    PG: { 20: "propylene-glycol-20-technical-grade", 30: "30-propylene-glycol-technical-grade", 40: "40-propylene-glycol-technical-grade", 50: "50-propylene-glycol-technical-grade", 60: "propylene-glycol-60-technical-grade" },
    EG: { 30: "ethylene-glycol-30-70", 40: "ethylene-glycol-40-60", 50: "ethylene-glycol-50-50", 60: "ethylene-glycol-60-40" },
  };

  /* id = the slug the old calculator used (launcher links depend on it). sg is used only where there is no
     density table. buy = the product handle for this stock. */
  function c(id, name, basis, stock, o) {
    o = o || {};
    return {
      id: id,
      name: name,
      basis: basis,
      stock: stock,
      sg: o.sg || null,
      species: o.species || null,
      family: o.family || o.species || null,
      purity: o.purity || 100,
      needsStock: !!o.needsStock,
      poolAcid: !!o.poolAcid,
      maxWv: o.maxWv || null,
      ppm: !!o.ppm,
      hazard: o.hazard,
      stocked: !!o.buy,
      buy: o.buy || null,
    };
  }
  // prettier-ignore
  var CHEMICALS = [
    c("acetic-acid-100-glacial", "Acetic Acid, Glacial (100%)", "ww", 100, { species: "AcOH", hazard: "acid", buy: "acetic-acid-glacial-technical" }),
    c("acetic-acid-30-industrial-vinegar", "Vinegar 30% (Industrial)", "ww", 30, { species: "AcOH", hazard: "acid", buy: "30-vinegar-concentrated-industrial-strength" }),
    c("acetic-acid-75-75-vinegar", "Vinegar 75%", "ww", 75, { species: "AcOH", hazard: "acid", buy: "75-vinegar" }),
    c("acetone-99-5", "Acetone 99.5%", "vv", 99.5, { sg: 0.791, hazard: "flammable", buy: "acetone-technical-grade" }),
    c("aluminum-sulfate-48", "Aluminum Sulfate 48% (Liquid Alum)", "ww", 48, { sg: 1.335, hazard: "acid", buy: "aluminum-sulfate-50" }),
    c("ammonia-household-5-10", "Household Ammonia (~10%)", "ww", 10, { species: "NH3", hazard: "ammonia" }),
    c("ammonium-hydroxide-28", "Ammonium Hydroxide 28–29%", "ww", 28, { species: "NH3", hazard: "ammonia", buy: "ammonium-hydroxide-29-technical-grade" }),
    c("bleach-household-5-25", "Bleach 5.25% (Household)", "ww", 5.25, { species: "NaOCl", hazard: "bleach", ppm: true, buy: "sodium-hypochlorite-5-25" }),
    c("calcium-hypochlorite-65-pool-shock", "Calcium Hypochlorite 65% (Pool Shock)", "solid", 10, { purity: 65, maxWv: 10, hazard: "bleach", ppm: true }),
    c("citric-acid-50", "Citric Acid 50% Solution", "ww", 50, { sg: 1.22, family: "Citric", hazard: "mildAcid", buy: "citric-acid-50-solution" }),
    c("denatured-alcohol-190-proof-95", "Denatured Alcohol 190 Proof (95%)", "vv", 95, { sg: 0.81, hazard: "flammable", buy: "denatured-alcohol-190-proof-3a" }),
    c("denatured-alcohol-200-proof-100", "Denatured Alcohol 200 Proof", "vv", 100, { sg: 0.789, hazard: "flammable", buy: "denatured-alcohol-200-proof-3a" }),
    c("ethylene-glycol-100", "Ethylene Glycol 100%", "vv", 100, { sg: 1.113, family: "EG", hazard: "toxic", buy: "ethylene-glycol-acs-grade" }),
    c("ferric-chloride-40", "Ferric Chloride 40%", "ww", 40, { species: "FeCl3", hazard: "acid", buy: "ferric-chloride-40" }),
    c("formic-acid-85", "Formic Acid 85%", "ww", 85, { species: "HCOOH", hazard: "acid" }),
    c("glycerin-99-7", "Glycerin 99.7%", "ww", 99.7, { species: "Glycerin", hazard: "mild", buy: "glycerin-usp-grade" }),
    c("hydrochloric-acid-31-muriatic", "Muriatic Acid 31.45% (Hydrochloric)", "ww", 31.45, { species: "HCl", hazard: "acid", poolAcid: true, buy: "hydrochloric-acid-31-technical-grade" }),
    c("hydrochloric-acid-37-acs", "Hydrochloric Acid 37% (ACS)", "ww", 37, { species: "HCl", hazard: "acid", poolAcid: true, buy: "hydrochloric-acid-37-acs-reagent-grade" }),
    c("hydrogen-peroxide-12", "Hydrogen Peroxide 12%", "ww", 12, { species: "H2O2", hazard: "peroxide", ppm: true, buy: "hydrogen-peroxide-12-technical-grade" }),
    c("hydrogen-peroxide-30", "Hydrogen Peroxide 30%", "ww", 30, { species: "H2O2", hazard: "peroxide", ppm: true, buy: "hydrogen-peroxide-30-technical" }),
    c("hydrogen-peroxide-35", "Hydrogen Peroxide 35%", "ww", 35, { species: "H2O2", hazard: "peroxide", ppm: true }),
    c("isopropyl-alcohol-70", "Isopropyl Alcohol 70%", "vv", 70, { sg: 0.877, family: "IPA", hazard: "flammable", buy: "isopropyl-alcohol-70-usp-grade" }),
    c("isopropyl-alcohol-91", "Isopropyl Alcohol 91%", "vv", 91, { sg: 0.82, family: "IPA", hazard: "flammable", buy: "isopropyl-alcohol-91" }),
    c("isopropyl-alcohol-99", "Isopropyl Alcohol 99%", "vv", 99, { sg: 0.786, family: "IPA", hazard: "flammable", buy: "isopropyl-alcohol-99" }),
    c("lactic-acid-88", "Lactic Acid 88%", "ww", 88, { sg: 1.209, hazard: "mildAcid" }),
    c("mek-99", "MEK 99% (Methyl Ethyl Ketone)", "vv", 99, { sg: 0.805, hazard: "flammable", buy: "methyl-ethyl-ketone-mek" }),
    c("methanol-99-8", "Methanol 99.8%", "vv", 99.8, { sg: 0.791, hazard: "methanol", buy: "methanol-technical-grade" }),
    c("nitric-acid-65", "Nitric Acid 65%", "ww", 65, { species: "HNO3", hazard: "oxidizingAcid", buy: "nitric-acid-65" }),
    c("oxalic-acid-solid-as-10-w-v", "Oxalic Acid (crystals)", "solid", 10, { maxWv: 10, hazard: "toxicSolid", buy: "oxalic-acid-dihydrate-ethanedioic-acid" }),
    c("phosphoric-acid-75", "Phosphoric Acid 75%", "ww", 75, { species: "H3PO4", hazard: "acid", buy: "phosphoric-acid-75" }),
    c("phosphoric-acid-85", "Phosphoric Acid 85%", "ww", 85, { species: "H3PO4", hazard: "acid", buy: "phosphoric-acid-85-technical-grade" }),
    c("potassium-hydroxide-45", "Potassium Hydroxide 45%", "ww", 45, { species: "KOH", hazard: "base", buy: "potassium-hydroxide-45-solution-technical-grade-caustic-potash-liquid-koh" }),
    c("propylene-glycol-100", "Propylene Glycol 100%", "vv", 100, { sg: 1.036, family: "PG", hazard: "mild", buy: "propylene-glycol-usp-grade" }),
    c("sodium-hydroxide-25", "Sodium Hydroxide 25%", "ww", 25, { species: "NaOH", hazard: "base", buy: "sodium-hydroxide-25" }),
    c("sodium-hydroxide-50", "Sodium Hydroxide 50% (Caustic Soda)", "ww", 50, { species: "NaOH", hazard: "base", buy: "sodium-hydroxide-50-membrane-grade-caustic-soda-lye" }),
    c("sodium-hypochlorite-12-5", "Bleach 12.5% (Sodium Hypochlorite)", "ww", 12.5, { species: "NaOCl", hazard: "bleach", ppm: true, buy: "sodium-hypochlorite-12-5" }),
    c("sulfuric-acid-93", "Sulfuric Acid 93%", "ww", 93, { species: "H2SO4", hazard: "acid", buy: "sulfuric-acid-93-technical-grade" }),
    c("sulfuric-acid-98", "Sulfuric Acid 98%", "ww", 98, { species: "H2SO4", hazard: "acid" }),
    c("trisodium-phosphate-solid-as-10-w-v", "Trisodium Phosphate (TSP)", "solid", 10, { maxWv: 10, hazard: "alkaliSolid", buy: "trisodium-phosphate-dodecahydrate-acs-reagent-grade" }),
    c("vinegar-white-5", "White Vinegar 5%", "ww", 5, { species: "AcOH", hazard: "mildAcid", buy: "5-acetic-acid-food-grade" }),
    c("vinegar-cleaning-6", "Cleaning Vinegar 6%", "ww", 6, { species: "AcOH", hazard: "mildAcid" }),
    c("other", "Other (not listed)", "vv", 100, { hazard: "other", needsStock: true }),
  ];
  /* The old calculator also accepted these slugs (its names, slugified). */
  var ALIASES = {
    "acetic-acid-100-glacial": ["acetic-acid-100"],
    "ammonia-household-5-10": ["ammonia-household"],
    "bleach-household-5-25": ["sodium-hypochlorite-5-25"],
    "ammonium-hydroxide-28": ["ammonium-hydroxide-29"],
  };

  var HAZARD = {
    acid: "Add the acid slowly to the water, never water to the acid. Wear goggles, a face shield and acid-resistant gloves.",
    oxidizingAcid:
      "Add the acid slowly to the water, never water to the acid. Keep away from alcohols and organics. Wear goggles, a face shield and acid-resistant gloves.",
    mildAcid:
      "Add it to the water and wear goggles and gloves. Never mix with bleach: acids release chlorine gas.",
    base: "Add the caustic slowly to cold water, never water to the caustic: it gets hot. Wear goggles, a face shield and gloves.",
    peroxide:
      "Add the peroxide to the water. Strong peroxide burns skin and bleaches clothing: wear goggles and gloves.",
    bleach:
      "Never mix with acids or ammonia: it releases toxic chlorine or chloramine gas. Wear goggles and gloves.",
    ammonia:
      "Never mix with bleach: it releases toxic chloramine gas. Work where there is good ventilation.",
    flammable:
      "Flammable: no flames, sparks or pilot lights nearby, and mix where there is good ventilation.",
    methanol:
      "Flammable and toxic if swallowed or absorbed: no flames nearby, wear nitrile gloves, ventilate.",
    toxic:
      "Toxic if swallowed: keep away from children and pets, and label the container.",
    mild: "Low hazard, but wear gloves and goggles and label the container.",
    other:
      "Check the SDS before you mix: it lists the hazards, the protective gear, and whether the chemical goes into the water or the water into it.",
    toxicSolid:
      "Harmful if swallowed and irritating to skin and eyes: wear gloves, goggles and a dust mask while weighing, and dissolve the crystals in warm water.",
    alkaliSolid:
      "Strongly alkaline: wear gloves and goggles, and stir the powder into the water, not water onto the powder.",
  };

  function slugify(v) {
    return String(v || "")
      .toLowerCase()
      .replace(/%/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }
  var INDEX = {};
  CHEMICALS.forEach(function (x) {
    INDEX[x.id] = x;
    INDEX[slugify(x.name)] = INDEX[slugify(x.name)] || x;
    (ALIASES[x.id] || []).forEach(function (a) {
      INDEX[a] = x;
    });
  });
  /* names of the old calculator, so ?chemical=<old name> still resolves */
  [
    ["Acetic Acid 100% (Glacial)", "acetic-acid-100-glacial"],
    ["Hydrogen Peroxide 30%", "hydrogen-peroxide-30"],
    ["Sodium Hypochlorite 12.5%", "sodium-hypochlorite-12-5"],
    ["Hydrochloric Acid 31% (Muriatic)", "hydrochloric-acid-31-muriatic"],
  ].forEach(function (p) {
    INDEX[slugify(p[0])] = INDEX[p[1]];
  });

  function find(idOrName) {
    return INDEX[idOrName] || INDEX[slugify(idOrName)] || null;
  }

  function density(species, pct) {
    if (species === "NaOCl") return 1 + 0.0152 * pct;
    var t = DENSITY[species];
    if (!t) return null;
    if (pct <= t[0][0]) return t[0][1];
    for (var i = 1; i < t.length; i++) {
      if (pct <= t[i][0]) {
        var a = t[i - 1],
          b = t[i];
        return a[1] + ((pct - a[0]) * (b[1] - a[1])) / (b[0] - a[0]);
      }
    }
    return t[t.length - 1][1];
  }

  function stockDensity(chem, stock) {
    return chem.species ? density(chem.species, stock) : chem.sg;
  }

  var UNIT_L = { gal: GAL_L, qt: QT_L, L: 1, mL: 0.001, floz: FLOZ_L };
  function toLiters(v, unit) {
    return UNIT_L[unit] ? v * UNIT_L[unit] : NaN;
  }
  function isMetric(unit) {
    return unit === "L" || unit === "mL";
  }

  function trim(n, d) {
    var v = Number(n.toFixed(d));
    return Math.abs(v) >= 1000
      ? v.toLocaleString("en-US", { maximumFractionDigits: d })
      : String(v);
  }
  function quarters(n) {
    var q = Math.round(n * 4) / 4;
    var whole = Math.floor(q),
      frac = q - whole;
    var f = frac === 0.25 ? "¼" : frac === 0.5 ? "½" : frac === 0.75 ? "¾" : "";
    return (whole ? String(whole) : f ? "" : "0") + f;
  }
  /* A volume the way someone will measure it, in the unit system they chose. */
  function formatVolume(L, unit) {
    if (!(L * 1000 >= 0.1)) return "<0.1 mL";
    if (isMetric(unit))
      return L >= 1
        ? trim(L, 2) + " L"
        : trim(L * 1000, L * 1000 < 10 ? 1 : 0) + " mL";
    var floz = L / FLOZ_L,
      mL = L * 1000;
    if (L >= GAL_L) return trim(L / GAL_L, 2) + " gal";
    if (floz >= 32) return trim(L / QT_L, 2) + " qt";
    if (floz >= 1) return trim(floz, 1) + " fl oz";
    if (mL >= TBSP_ML * 0.875) return quarters(mL / TBSP_ML) + " Tbsp";
    if (mL >= TSP_ML / 8) return quarters(mL / TSP_ML) + " tsp";
    return trim(mL, 2) + " mL";
  }
  function formatMass(g, unit) {
    if (isMetric(unit))
      return g >= 1000
        ? trim(g / 1000, 2) + " kg"
        : trim(g, g < 10 ? 1 : 0) + " g";
    return g >= LB_G ? trim(g / LB_G, 2) + " lb" : trim(g / OZ_G, 2) + " oz";
  }

  /* "Bleach 12.5%" with the strength the visitor actually has, e.g. "Bleach 11%" */
  function labelFor(chem, stock) {
    if (chem.id === "other")
      return stock == null || stock === ""
        ? "your chemical"
        : "your " + trim(Number(stock), 2) + "% chemical";
    var base = chem.name.replace(/\s*\(.*\)\s*$/, "");
    if (
      stock == null ||
      stock === "" ||
      Math.abs(Number(stock) - chem.stock) < 1e-9
    )
      return base;
    var s = trim(Number(stock), 2) + "%";
    return /[\d.]+%/.test(base) ? base.replace(/[\d.]+%/, s) : s + " " + base;
  }

  /* Pool water: the alkalinity (meq/L) acid has to remove to move pH from phNow to target, holding dissolved
     inorganic carbon and cyanuric acid constant (no CO2 lost while dosing). 25 C, pool ionic strength:
     pK1 6.3, pK2 10.25 for carbonic acid, pKa 6.88 for cyanuric acid. Borates are not modelled. */
  function poolDose(phNow, target, ta, cya) {
    if (
      !(phNow >= 6.8 && phNow <= 8.6) ||
      !(target > 0) ||
      !(ta > 0 && ta <= 400) ||
      !(cya >= 0 && cya <= 150)
    )
      return {
        error: "invalid-input",
        message:
          "Enter pH between 6.8 and 8.6, total alkalinity up to 400 ppm and cyanuric acid up to 150 ppm.",
      };
    if (target >= phNow - 1e-9)
      return {
        error: "ph-up",
        message:
          "Acid only lowers pH. To raise it, use soda ash (sodium carbonate).",
      };
    if (target < 7.0)
      return {
        error: "ph-too-low",
        message:
          "Keep pool water at pH 7.0 or above: lower pH etches plaster and corrodes metal.",
      };
    var K1 = Math.pow(10, -6.3),
      K2 = Math.pow(10, -10.25),
      Kc = Math.pow(10, -6.88),
      Kw = 1e-14;
    var cyaM = cya / 129070; /* mg/L to mol/L */
    var parts = function (ph) {
      var h = Math.pow(10, -ph),
        d = h * h + K1 * h + K1 * K2;
      return {
        h: h,
        carb: (K1 * h + 2 * K1 * K2) / d,
        cyan: Kc / (h + Kc),
        oh: Kw / h,
      };
    };
    var a = parts(phNow),
      b = parts(target);
    var taEq = ta / 50043; /* ppm as CaCO3 to eq/L */
    var carbEq = taEq - cyaM * a.cyan;
    if (!(carbEq > 0))
      return {
        error: "invalid-input",
        message:
          "Total alkalinity is lower than the cyanuric acid share; check the readings.",
      };
    var dic = (carbEq - a.oh + a.h) / a.carb;
    var alkAfter = dic * b.carb + cyaM * b.cyan + b.oh - b.h;
    var drop = taEq - alkAfter;
    return {
      phNow: phNow,
      meqPerL: drop * 1000,
      taDrop: drop * 50043,
      carbAlk: carbEq * 50043,
    };
  }

  /* Why the numbers are what they are, in one line, shown under every answer. */
  function basisNote(r) {
    if (r.mode === "pool")
      return (
        "Pool water chemistry: the acid it takes to move pH at your total alkalinity (less the cyanuric acid share), at 25 °C. It also lowers total alkalinity by about " +
        trim(r.taDrop, 0) +
        " ppm."
      );
    if (r.chem.id === "other")
      return "Approximate: by volume (C1V1 = C2V2), without density data. For acids, bases and other liquids sold by weight this can be well off; check the supplier's dilution chart.";
    var n = r.chem.name
      .replace(/\s*\(.*\)\s*$/, "")
      .replace(/\s*[\d.]+%.*$/, "");
    if (r.mode === "ratio")
      return (
        "By volume: 1 part concentrate to " +
        trim(r.target, 3) +
        " parts water, measured in the same cup or jug."
      );
    if (r.basis === "solid")
      return (
        "By weight of powder (w/v): " +
        n +
        " is weighed as sold and dissolved, then topped up to the full batch."
      );
    if (r.mode === "ppm")
      return r.chem.species === "H2O2"
        ? "ppm of hydrogen peroxide (mg per liter), from the peroxide strength by weight and its density."
        : "ppm of available chlorine (mg per liter), from the bleach strength by weight and its density.";
    if (r.basis === "vv")
      return "By volume (v/v): alcohols, solvents and glycols are labeled as % by volume, so this is C1V1 = C2V2, topped up to volume.";
    return (
      "By weight (w/w): " +
      n +
      " is sold as % by weight, so this uses its measured density at each strength, not a simple volume ratio."
    );
  }

  function hazardNote(chem) {
    return HAZARD[chem.hazard] || HAZARD.mild;
  }

  function err(code, message) {
    return { ok: false, error: code, message: message };
  }

  /* opts: { chemical, stock?, mode: percent|ratio|ppm, target, volume, unit } */
  function solve(opts) {
    var chem =
      typeof opts.chemical === "string" ? find(opts.chemical) : opts.chemical;
    if (!chem)
      return err("unknown-chemical", "Choose a chemical from the list.");
    var mode = opts.mode || "percent";
    var target = Number(opts.target),
      volume = Number(opts.volume);
    var stock =
      opts.stock != null && opts.stock !== "" ? Number(opts.stock) : chem.stock;
    var V = toLiters(volume, opts.unit);
    if (
      !(target > 0) ||
      !(volume > 0) ||
      !(V > 0) ||
      !(stock > 0) ||
      stock > 100
    )
      return err(
        "invalid-input",
        "Enter a strength and a batch size above zero.",
      );
    if (chem.needsStock && (opts.stock == null || opts.stock === ""))
      return err(
        "stock-required",
        "Enter the strength on your label in “Strength you have”.",
      );
    if (mode === "ppm" && !chem.ppm)
      return err(
        "mode-unsupported",
        "ppm works for bleach, pool shock and hydrogen peroxide. Use % for " +
          chem.name +
          ".",
      );
    if (mode === "pool" && !chem.poolAcid)
      return err(
        "mode-unsupported",
        "The pool pH mode works for muriatic (hydrochloric) acid.",
      );
    if (
      mode !== "percent" &&
      mode !== "ratio" &&
      mode !== "ppm" &&
      mode !== "pool"
    )
      return err("invalid-input", "Unknown mode.");

    var r = {
      ok: true,
      chem: chem,
      basis: chem.basis,
      mode: mode,
      stock: stock,
      target: target,
      unit: opts.unit,
      total: { L: V },
      conc: { L: null, g: null },
      water: { L: null },
      finalWw: null,
      finalVv: null,
      topUp: false,
      notes: [],
    };
    var rho1 = chem.basis === "solid" ? null : stockDensity(chem, stock);
    var table = chem.species && DENSITY[chem.species];
    if (table && stock > table[table.length - 1][0] + 0.05)
      return err(
        "stock-out-of-range",
        "Density data for " +
          chem.name.replace(/\s*[\d.]+%.*$/, "") +
          " goes up to " +
          table[table.length - 1][0] +
          "%. Check the strength on your label.",
      );

    if (mode === "pool") {
      var pd = poolDose(
        Number(opts.phNow),
        target,
        Number(opts.ta),
        opts.cya == null || opts.cya === "" ? 0 : Number(opts.cya),
      );
      if (pd.error) return err(pd.error, pd.message);
      /* every equivalent of alkalinity removed takes one mole of HCl */
      var hclG = (pd.meqPerL * V * 36.461) / 1000;
      r.conc.g = hclG / (stock / 100);
      r.conc.L = r.conc.g / 1000 / rho1;
      r.water.L = V;
      r.phNow = pd.phNow;
      r.taDrop = pd.taDrop;
      r.carbAlk = pd.carbAlk;
      r.split = pd.phNow - target > 0.3 + 1e-9;
      if (target < 7.2)
        r.notes.push("Most pools are kept between pH 7.4 and 7.6.");
      r.notes.push(
        "Borates (some salt pools and pool products add them) buffer pH too; with borates you may need more. Retest before adding more.",
      );
      r.hazard =
        "Add the acid slowly in front of a return jet with the pump running, never into the skimmer, and never mix it with chlorine products. Wear goggles and acid-resistant gloves.";
      return r;
    }

    if (chem.basis === "solid") {
      var wv =
        mode === "percent"
          ? target
          : mode === "ratio"
            ? chem.stock / (1 + target)
            : null;
      if (mode === "ppm") {
        r.conc.g = (target * V) / 1000 / (chem.purity / 100);
        r.finalWw = target / 10000;
        if (r.conc.g / (V * 10) > chem.maxWv)
          return err(
            "above-max",
            "That much " +
              chem.name +
              " will not dissolve. Check the target ppm.",
          );
      } else {
        if (wv > chem.maxWv)
          return err(
            "above-max",
            "This calculator handles " +
              chem.name +
              " up to " +
              chem.maxWv +
              "% w/v. Check the SDS for solubility before going stronger.",
          );
        r.conc.g = ((wv / 100) * V * 1000) / (chem.purity / 100);
        r.finalWw = wv;
        if (mode === "ratio")
          r.notes.push(
            "Ratio 1:" +
              trim(target, 3) +
              " is read as a " +
              chem.stock +
              "% w/v solution diluted 1:" +
              trim(target, 3) +
              ", which is " +
              trim(wv, 2) +
              "% w/v.",
          );
      }
      if (chem.purity < 100)
        r.notes.push("Assumes " + chem.purity + "% available chlorine.");
      if (/oxalic/.test(chem.id))
        r.notes.push(
          "This is the weight of crystals as sold (dihydrate). If your recipe gives anhydrous oxalic acid, use 1.40 times as much.",
        );
      r.water.L = V;
      r.topUp = true;
    } else if (mode === "ppm") {
      /* mg/L: available chlorine for hypochlorite (NaOCl x 0.9525), the H2O2 itself for peroxide;
         the stock supplies stock% x rho x 10 g/L of active ingredient per litre */
      var peroxide = chem.species === "H2O2";
      var clPerL = stock * rho1 * 10 * (peroxide ? 1 : NAOCL_TO_CL2) * 1000;
      r.conc.L = (target * V) / clPerL;
      if (r.conc.L >= V)
        return err(
          "target-too-strong",
          "That is stronger than the " +
            (peroxide ? "peroxide" : "bleach") +
            " you start with (" +
            trim(stock, 2) +
            "%).",
        );
      r.water.L = V - r.conc.L;
      r.finalWw = target / 10000;
      if (!peroxide && target > 10)
        r.notes.push(
          "Above about 10 ppm, keep people out of treated pool water until it reads 1–3 ppm again.",
        );
      r.notes.push(
        (peroxide ? "Peroxide" : "Bleach") +
          " loses strength with age, so confirm with a test kit or strips.",
      );
    } else if (mode === "ratio") {
      r.conc.L = V / (1 + target);
      r.water.L = V - r.conc.L;
      var mc = r.conc.L * rho1,
        mw = r.water.L * WATER;
      r.finalWw =
        chem.basis === "ww" ? (100 * mc * (stock / 100)) / (mc + mw) : null;
      r.finalVv = chem.basis === "vv" ? stock / (1 + target) : null;
    } else {
      if (target >= stock)
        return err(
          "target-too-strong",
          "The target has to be weaker than the stock (" +
            trim(stock, 2) +
            "%).",
        );
      if (chem.basis === "vv") {
        r.conc.L = (target * V) / stock;
        r.water.L = V - r.conc.L;
        r.topUp = true;
        r.finalVv = target;
        r.notes.push(
          "Alcohol and water shrink a little when mixed, so add water until you reach the full batch rather than measuring the water.",
        );
      } else {
        var rho2 = chem.species ? density(chem.species, target) : null;
        var concKg, finalKg;
        if (rho2) {
          finalKg = V * rho2;
          concKg = (target * finalKg) / stock;
        } else {
          /* no density table: ideal mixing with water (concentrate + water volumes add up) */
          concKg = V / (1 / rho1 + (stock / target - 1) / WATER);
          finalKg = concKg * (stock / target);
        }
        r.conc.L = concKg / rho1;
        r.water.L = (finalKg - concKg) / WATER;
        r.finalWw = target;
        r.topUp = true;
      }
    }
    if (r.conc.L != null) r.conc.g = r.conc.L * rho1 * 1000;
    r.hazard = hazardNote(chem);
    return r;
  }

  /* ---------- packs ---------- */

  var NUM = "(\\d+(?:\\.\\d+)?)";
  function unitOf(word) {
    word = word.toLowerCase();
    if (/^gal/.test(word)) return ["gal", 1];
    if (/^quart|^qt/.test(word)) return ["gal", 0.25];
    if (/^pint|^pt/.test(word)) return ["gal", 0.125];
    if (/^lit|^l$/.test(word)) return ["gal", 1 / GAL_L];
    if (/^lb/.test(word)) return ["lb", 1];
    return null;
  }
  /* "4 x 5 Gallon Pails" -> {qty: 20, unit: "gal"}; spray bottles and pumps are not packs of stock. */
  function parsePack(title) {
    var s = String(title || "")
      .split(" / ")
      .pop()
      .trim();
    if (/spray|pump|single|default title|^\d+-pack$/i.test(s)) return null;
    var m = s.match(
      new RegExp(
        "\\(\\s*" + NUM + "\\s*(lbs?|quarts?|gallons?|liters?)\\.?\\s*\\)",
        "i",
      ),
    );
    if (m) {
      var u0 = unitOf(m[2]);
      return { qty: Number(m[1]) * u0[1], unit: u0[0] };
    }
    m = s.match(
      new RegExp("^" + NUM + "\\s*x\\s*" + NUM + "\\s*([a-z]+)", "i"),
    );
    if (m && unitOf(m[3])) {
      var u1 = unitOf(m[3]);
      return { qty: Number(m[1]) * Number(m[2]) * u1[1], unit: u1[0] };
    }
    m = s.match(new RegExp("^" + NUM + "\\s*([a-z]+)\\s*x\\s*" + NUM, "i"));
    if (m && unitOf(m[2])) {
      var u2 = unitOf(m[2]);
      return { qty: Number(m[1]) * Number(m[3]) * u2[1], unit: u2[0] };
    }
    m = s.match(new RegExp("^" + NUM + "\\s*([a-z]+)", "i"));
    if (m && unitOf(m[2])) {
      var u3 = unitOf(m[2]);
      return { qty: Number(m[1]) * u3[1], unit: u3[0] };
    }
    return null;
  }

  /* need in gal or lb; variants [{id, title, price (dollars), available}].
     best = cheapest way to cover the need (up to 10 of one pack); value = a bigger pack that is at least 15%
     cheaper per unit and no more than 4x the need. */
  function choosePack(need, unit, variants) {
    var packs = [];
    (variants || []).forEach(function (v) {
      var p = parsePack(v.title);
      if (p && p.unit === unit && v.available !== false && v.price > 0)
        packs.push({ variant: v, qty: p.qty, price: Number(v.price) });
    });
    if (!packs.length || !(need > 0)) return null;
    var best = null;
    packs.forEach(function (p) {
      var count = Math.ceil(need / p.qty - 1e-9);
      if (count > 10) return;
      var cost = count * p.price;
      /* same cost: more product wins, then fewer units */
      var tie = best && Math.abs(cost - best.cost) < 1e-9;
      if (
        !best ||
        cost < best.cost - 1e-9 ||
        (tie && count * p.qty > best.count * best.qty + 1e-9) ||
        (tie &&
          Math.abs(count * p.qty - best.count * best.qty) < 1e-9 &&
          count < best.count)
      )
        best = {
          variant: p.variant,
          count: count,
          cost: cost,
          qty: p.qty,
          perUnit: p.price / p.qty,
        };
    });
    if (!best) return { best: null, value: null, quote: true };
    var value = null;
    packs.forEach(function (p) {
      if (p.qty < need || p.qty > need * 4 || p.variant === best.variant)
        return;
      var per = p.price / p.qty;
      if (
        per <= (best.cost / (best.count * best.qty)) * 0.85 &&
        (!value || per < value.perUnit)
      )
        value = {
          variant: p.variant,
          count: 1,
          cost: p.price,
          qty: p.qty,
          perUnit: per,
        };
    });
    return {
      best: best,
      value: value,
      quote: best.count > 1 && best.qty >= 55,
    };
  }

  /* The size picker in the buy card: single packs that cover the need, one per amount (the cheapest when two
     packs hold the same, e.g. 2 Gallons vs 8 Quarts), smallest first, cart-able only (<= CART_MAX), at most 6;
     the cheapest fit is always in the list and marked best (it can be a multiple, e.g. 2 x 1 Gallon). */
  var CART_MAX = { gal: 55, lb: 220 };
  function packOptions(need, unit, variants) {
    var pick = choosePack(need, unit, variants);
    var bestV = pick && pick.best ? pick.best.variant : null;
    var byTotal = {};
    (variants || []).forEach(function (v) {
      var p = parsePack(v.title);
      if (!p || p.unit !== unit || v.available === false || !(v.price > 0))
        return;
      if (p.qty < need - 1e-9 || p.qty > CART_MAX[unit]) return;
      var key = p.qty.toFixed(4),
        price = Number(v.price);
      if (!byTotal[key] || price < byTotal[key].cost)
        byTotal[key] = {
          variant: v,
          count: 1,
          qty: p.qty,
          total: p.qty,
          cost: price,
          perUnit: price / p.qty,
          cartable: true,
          best: false,
        };
    });
    var rows = Object.keys(byTotal).map(function (k) {
      return byTotal[k];
    });
    /* drop a size when a bigger one costs the same or less (2 Quarts $27.15 vs 1 Gallon $27.10) */
    rows = rows.filter(function (r) {
      return !rows.some(function (o) {
        return o !== r && o.total > r.total + 1e-9 && o.cost <= r.cost + 1e-9;
      });
    });
    rows.sort(function (a, b) {
      return a.total - b.total || a.cost - b.cost;
    });
    rows = rows.slice(0, 6);
    if (
      bestV &&
      pick.best.count * pick.best.qty <= CART_MAX[unit] &&
      pick.best.count <= 4
    ) {
      var hit = rows.filter(function (r) {
        return r.variant === bestV;
      })[0];
      if (!hit) {
        hit = {
          variant: bestV,
          count: pick.best.count,
          qty: pick.best.qty,
          total: pick.best.count * pick.best.qty,
          cost: pick.best.cost,
          perUnit: pick.best.perUnit,
          cartable: true,
        };
        rows = rows
          .filter(function (r) {
            return Math.abs(r.total - hit.total) > 1e-9;
          })
          .slice(0, 5)
          .concat([hit]);
        rows.sort(function (a, b) {
          return a.total - b.total || a.cost - b.cost;
        });
      }
      hit.best = true;
    }
    return rows;
  }

  /* Nicknames people type. Exact matches rank first; family words let "IPA 91" or "h2o2 35" pick a strength. */
  // prettier-ignore
  var NICKNAMES = {
    "hydrochloric-acid-31-muriatic": ["muriatic", "muriatic acid", "hcl", "hydrochloric", "hydrochloric acid", "pool acid"],
    "hydrochloric-acid-37-acs": ["reagent hcl", "hcl 37"],
    "sodium-hydroxide-50": ["lye", "caustic soda", "caustic", "naoh", "sodium hydroxide", "liquid lye"],
    "potassium-hydroxide-45": ["koh", "caustic potash", "potash", "potassium hydroxide"],
    "sulfuric-acid-93": ["battery acid", "sulfuric", "sulphuric", "sulfuric acid", "h2so4", "drain acid"],
    "calcium-hypochlorite-65-pool-shock": ["pool shock", "shock", "cal hypo", "calcium hypochlorite"],
    "sodium-hypochlorite-12-5": ["bleach", "pool chlorine", "liquid chlorine", "sodium hypochlorite", "naocl", "chlorine"],
    "bleach-household-5-25": ["household bleach", "clorox", "laundry bleach"],
    "isopropyl-alcohol-99": ["rubbing alcohol", "ipa", "isopropanol", "isopropyl", "isopropyl alcohol"],
    "hydrogen-peroxide-30": ["peroxide", "h2o2", "hydrogen peroxide"],
    "hydrogen-peroxide-35": ["food grade peroxide"],
    "ethylene-glycol-100": ["antifreeze", "coolant", "ethylene glycol"],
    "propylene-glycol-100": ["rv antifreeze", "propylene glycol", "pg", "food grade antifreeze"],
    "oxalic-acid-solid-as-10-w-v": ["wood bleach", "oxalic", "oxalic acid", "deck brightener"],
    "acetic-acid-30-industrial-vinegar": ["vinegar", "cleaning vinegar", "industrial vinegar", "horticultural vinegar"],
    "vinegar-white-5": ["white vinegar"],
    "acetic-acid-100-glacial": ["glacial", "glacial acetic", "acetic acid"],
    "trisodium-phosphate-solid-as-10-w-v": ["tsp", "trisodium phosphate", "paint prep"],
    "phosphoric-acid-85": ["phosphoric", "phosphoric acid", "rust remover", "h3po4"],
    "nitric-acid-65": ["nitric", "nitric acid", "hno3"],
    "ferric-chloride-40": ["ferric", "ferric chloride", "etchant", "pcb etchant"],
    "citric-acid-50": ["citric", "citric acid", "descaler"],
    "glycerin-99-7": ["glycerol", "glycerine", "glycerin"],
    "methanol-99-8": ["methyl alcohol", "wood alcohol", "methanol"],
    "mek-99": ["methyl ethyl ketone", "mek"],
    "denatured-alcohol-190-proof-95": ["ethanol", "denatured", "denatured alcohol"],
    "ammonium-hydroxide-28": ["ammonia", "aqua ammonia", "ammonium hydroxide"],
    "ammonia-household-5-10": ["household ammonia"],
    "aluminum-sulfate-48": ["alum", "liquid alum", "aluminum sulfate"],
    "acetone-99-5": ["acetone"],
    "other": ["other", "not listed", "something else"],
  };
  // prettier-ignore
  var FAMILY_WORDS = {
    IPA: "ipa isopropyl isopropanol rubbing alcohol", H2O2: "peroxide h2o2 hydrogen", NaOH: "lye caustic soda naoh sodium hydroxide",
    KOH: "koh potash caustic potassium hydroxide", NH3: "ammonia ammonium", HCl: "hcl hydrochloric muriatic acid", H2SO4: "sulfuric sulphuric battery acid h2so4",
    HNO3: "nitric acid hno3", H3PO4: "phosphoric acid h3po4", NaOCl: "bleach chlorine sodium hypochlorite naocl", AcOH: "vinegar acetic acid",
    EG: "ethylene glycol antifreeze", PG: "propylene glycol antifreeze", Glycerin: "glycerin glycerol", Citric: "citric acid", FeCl3: "ferric chloride",
  };
  function words(t) {
    return String(t || "")
      .toLowerCase()
      .replace(/[^a-z0-9.]+/g, " ")
      .trim()
      .split(" ")
      .filter(Boolean);
  }
  function search(query) {
    var q = words(query);
    var order = CHEMICALS.slice().sort(function (a, b) {
      return (b.stocked ? 1 : 0) - (a.stocked ? 1 : 0);
    });
    if (!q.length) return order;
    var phrase = q.join(" ");
    var scored = order.map(function (chem, i) {
      var nick = NICKNAMES[chem.id] || [];
      var hay = words(
        chem.name +
          " " +
          nick.join(" ") +
          " " +
          (FAMILY_WORDS[chem.family] || ""),
      ).concat(String(chem.stock));
      var score = 0;
      if (nick.indexOf(phrase) >= 0) score = 100;
      else if (
        nick.some(function (n) {
          return n.indexOf(phrase) === 0;
        })
      )
        score = 80;
      var found = q.filter(function (w) {
        return hay.some(function (h) {
          return h === w || (w.length > 2 && h.indexOf(w) === 0);
        });
      }).length;
      if (!score && found === q.length) score = 50;
      else if (!score && found) score = 20 * (found / q.length);
      if (
        score &&
        q.some(function (w) {
          return /^\d/.test(w) && Number(w) === chem.stock;
        })
      )
        score += 30;
      return { chem: chem, score: score, i: i };
    });
    return scored
      .filter(function (x) {
        return x.score >= 20;
      })
      .sort(function (a, b) {
        return b.score - a.score || a.i - b.i;
      })
      .map(function (x) {
        return x.chem;
      });
  }

  function readyMade(id, target) {
    var chem = find(id);
    var fam = chem && READY[chem.family];
    if (!fam) return null;
    var keys = Object.keys(fam);
    for (var i = 0; i < keys.length; i++)
      if (
        Math.abs(Number(keys[i]) - Number(target)) < 0.01 &&
        fam[keys[i]] !== chem.buy
      )
        return fam[keys[i]];
    return null;
  }

  var core = {
    CHEMICALS: CHEMICALS,
    find: find,
    density: density,
    solve: solve,
    toLiters: toLiters,
    formatVolume: formatVolume,
    formatMass: formatMass,
    hazardNote: hazardNote,
    labelFor: labelFor,
    basisNote: basisNote,
    parsePack: parsePack,
    choosePack: choosePack,
    packOptions: packOptions,
    search: search,
    readyMade: readyMade,
  };
  window.__acDilute = core;
  if (typeof document === "undefined") return;

  /* ---------- DOM ---------- */
  var money = function (dollars) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(dollars);
  };
  var root =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
    "/";
  var productCache = {};
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function ga(name, params) {
    if (window.acGA4 && typeof window.acGA4.trackEvent === "function")
      window.acGA4.trackEvent(name, params);
  }
  /* product JSON with prices in dollars and the pack name (Variation option) as the variant title */
  /* the pack option is the one named "Variation" (Size x Variation on every product today) */
  function packIndex(p) {
    var names = (p.options || []).map(function (o) {
      return String(o && o.name ? o.name : o);
    });
    var i = names.findIndex(function (n) {
      return /variation|pack/i.test(n);
    });
    return i >= 0 ? i + 1 : 2;
  }
  function normalize(p) {
    var opt = "option" + packIndex(p);
    return {
      handle: p.handle,
      title: p.title,
      url: p.url || root + "products/" + p.handle,
      variants: (p.variants || []).map(function (v) {
        return {
          id: v.id,
          title: v[opt] || v.title,
          price: Number(v.price) / 100,
          available: v.available !== false,
        };
      }),
    };
  }
  function getProduct(handle, form) {
    if (!handle) return Promise.resolve(null);
    if (productCache[handle]) return productCache[handle];
    var tag =
      form &&
      form.closest("[data-ac-dilute]") &&
      form.closest("[data-ac-dilute]").querySelector("[data-buy-json]");
    if (tag) {
      try {
        var ssr = JSON.parse(tag.textContent);
        if (ssr && ssr.handle === handle)
          return (productCache[handle] = Promise.resolve(normalize(ssr)));
      } catch (e) {
        /* fall through to the fetch */
      }
    }
    productCache[handle] = fetch(root + "products/" + handle + ".js", {
      headers: { Accept: "application/json" },
      signal:
        window.AbortSignal && AbortSignal.timeout
          ? AbortSignal.timeout(8000)
          : undefined,
    })
      .then(function (r) {
        if (!r.ok) throw new Error("product " + handle + " " + r.status);
        return r.json();
      })
      .then(normalize)
      .catch(function () {
        return null; /* cached: one failed fetch per page view, not one per keystroke */
      });
    return productCache[handle];
  }
  function refreshHeader() {
    return fetch(root + "cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        document
          .querySelectorAll(
            "[data-header-cart-count], .cart-count-bubble span, [data-cart-count]",
          )
          .forEach(function (n) {
            n.textContent = cart.item_count;
            n.classList.remove("hidden");
          });
        document.dispatchEvent(
          new CustomEvent("ac:cart-updated", { detail: { cart: cart } }),
        );
      });
  }

  /* Searchable chemical box over the real <select> (which stays as the no-JS control): type a name or a
     nickname ("muriatic", "lye", "pool shock"), arrow keys move, Enter picks, Escape closes. */
  var comboCount = 0;
  function buildCombo(select) {
    var id = (select.id || "ac-dilute-chem") + "-q" + ++comboCount;
    var wrap = el("div", "ac-dilute__combo");
    var input = document.createElement("input");
    input.type = "text";
    input.id = id;
    input.className = "ac-dilute__combo-input";
    input.setAttribute("role", "combobox");
    input.setAttribute("aria-autocomplete", "list");
    input.setAttribute("aria-expanded", "false");
    input.setAttribute("aria-controls", id + "-list");
    input.setAttribute("autocomplete", "off");
    input.setAttribute("spellcheck", "false");
    input.placeholder = "Type a chemical, e.g. muriatic, lye, bleach";
    var list = el("ul", "ac-dilute__combo-list");
    list.id = id + "-list";
    list.setAttribute("role", "listbox");
    list.hidden = true;
    wrap.appendChild(input);
    wrap.appendChild(list);
    select.parentNode.insertBefore(wrap, select);
    select.hidden = true;
    select.setAttribute("tabindex", "-1");
    var label =
      select.form &&
      select.form.querySelector('label[for="' + select.id + '"]');
    if (label) label.setAttribute("for", id);
    var items = [],
      active = -1,
      current = null;

    function render(q) {
      items = search(q);
      list.innerHTML = "";
      items.forEach(function (chem, i) {
        var li = el("li", "ac-dilute__combo-opt", chem.name);
        li.id = id + "-o" + i;
        li.setAttribute("role", "option");
        li.setAttribute(
          "aria-selected",
          String(current && chem.id === current.id),
        );
        if (chem.stocked)
          li.appendChild(el("span", "ac-dilute__combo-tag", "We sell"));
        li.addEventListener("mousedown", function (e) {
          e.preventDefault();
          pick(chem);
        });
        list.appendChild(li);
      });
      if (!items.length)
        list.appendChild(
          el(
            "li",
            "ac-dilute__combo-none",
            "No match. Pick “Other (not listed)” to use your own strength.",
          ),
        );
      setActive(items.length ? 0 : -1);
    }
    function setActive(i) {
      active = i;
      Array.prototype.forEach.call(list.children, function (li, j) {
        li.classList.toggle("is-active", j === i);
      });
      if (i >= 0) {
        input.setAttribute("aria-activedescendant", id + "-o" + i);
        var li = list.children[i];
        if (li && li.scrollIntoView) li.scrollIntoView({ block: "nearest" });
      } else input.removeAttribute("aria-activedescendant");
    }
    function open(q) {
      render(q);
      list.hidden = false;
      input.setAttribute("aria-expanded", "true");
    }
    function close() {
      list.hidden = true;
      input.setAttribute("aria-expanded", "false");
      input.removeAttribute("aria-activedescendant");
      if (current) input.value = current.name;
    }
    function pick(chem) {
      current = chem;
      input.value = chem.name;
      close();
      if (select.value !== chem.id) {
        select.value = chem.id;
        select.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
    input.addEventListener("focus", function () {
      input.select();
      open("");
    });
    input.addEventListener("input", function (e) {
      e.stopPropagation();
      open(input.value);
    });
    input.addEventListener("keydown", function (e) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        if (list.hidden) open(input.value);
        else if (items.length)
          setActive(
            (active + (e.key === "ArrowDown" ? 1 : items.length - 1)) %
              items.length,
          );
      } else if (e.key === "Enter") {
        if (!list.hidden && active >= 0) {
          e.preventDefault();
          pick(items[active]);
        }
      } else if (e.key === "Escape") {
        close();
      }
    });
    input.addEventListener("blur", function () {
      setTimeout(close, 120);
    });
    return {
      sync: function (chem) {
        current = chem;
        if (chem && document.activeElement !== input) input.value = chem.name;
      },
    };
  }

  /* params: initial state (the article embed passes its own); defaults to the page URL */
  function mount(form, params) {
    if (form.__acDilute) return;
    form.__acDilute = true;
    var scope = form.closest("[data-ac-dilute]") || form.parentNode;
    var $ = function (sel) {
      return form.querySelector(sel);
    };
    var f = {};
    [
      "chemical",
      "stock",
      "target",
      "volume",
      "unit",
      "phNow",
      "ta",
      "cya",
    ].forEach(function (k) {
      f[k] = $('[data-f="' + k + '"]');
    });
    var modes = form.querySelectorAll('[data-f="mode"]');
    var out = {
      msg: $("[data-msg]"),
      result: $("[data-result]"),
      headline: $("[data-headline]"),
      steps: $("[data-steps]"),
      figures: $("[data-figures]"),
      hazard: $("[data-hazard]"),
      notes: $("[data-notes]"),
      tank: $("[data-tank]"),
      keyConc: $("[data-key-conc]"),
      basis: $("[data-basis]"),
      targetLabel: $("[data-target-label]"),
      suffix: $("[data-target-suffix]"),
    };
    var buy = {
      root: scope.querySelector("[data-buy]"),
      body: scope.querySelector("[data-buy-body]"),
      alts: scope.querySelector("[data-buy-alts]"),
      cart: scope.querySelector("[data-cart]"),
    };
    var missing = "";
    var wasPool = false;
    var beforePool = null;
    var isEmbed = form.hasAttribute("data-embed");
    var gaTimer = 0;
    var CALC_KEYS = [
      "chemical",
      "stock",
      "mode",
      "target",
      "volume",
      "unit",
      "phNow",
      "ta",
      "cya",
    ];

    /* chemical list: what we sell first */
    var current = f.chemical.value;
    f.chemical.innerHTML = "";
    [
      ["We sell", true],
      ["Other chemicals", false],
    ].forEach(function (g) {
      var og = document.createElement("optgroup");
      og.label = g[0];
      CHEMICALS.filter(function (x) {
        return x.stocked === g[1];
      }).forEach(function (x) {
        var o = document.createElement("option");
        o.value = x.id;
        o.textContent = x.name;
        og.appendChild(o);
      });
      f.chemical.appendChild(og);
    });
    var combo = buildCombo(f.chemical);

    function mode() {
      for (var i = 0; i < modes.length; i++)
        if (modes[i].checked) return modes[i].value;
      return "percent";
    }
    function setMode(m) {
      for (var i = 0; i < modes.length; i++)
        modes[i].checked = modes[i].value === m;
    }
    function apply(params, isInitial) {
      var chem = params.get("chemical");
      if (chem != null) {
        var hit = find(chem);
        var fallback = find(form.getAttribute("data-default-chemical") || "");
        if (hit) f.chemical.value = hit.id;
        else if (fallback) f.chemical.value = fallback.id;
        if (!hit)
          missing =
            "We could not find “" +
            chem +
            "” in the list. Choose your chemical above.";
      } else if (isInitial) {
        var d = find(form.getAttribute("data-default-chemical") || current);
        if (d) f.chemical.value = d.id;
      }
      ["stock", "target", "volume", "phNow", "ta", "cya"].forEach(function (k) {
        if (params.get(k) != null) f[k].value = params.get(k);
      });
      if (params.get("unit") && UNIT_L[params.get("unit")])
        f.unit.value = params.get("unit");
      if (params.get("mode")) setMode(params.get("mode"));
    }

    function chemNow() {
      return find(f.chemical.value);
    }
    function syncControls(chem) {
      if (combo) combo.sync(chem);
      var ppmOk = !!(chem && chem.ppm);
      form.querySelectorAll("[data-ppm-only]").forEach(function (n) {
        n.hidden = !ppmOk;
      });
      if (!ppmOk && mode() === "ppm") setMode("percent");
      var poolOk = !!(chem && chem.poolAcid);
      form.querySelectorAll("[data-pool-only]").forEach(function (n) {
        n.hidden = !poolOk;
      });
      if (!poolOk && mode() === "pool") setMode("percent");
      var inPool = mode() === "pool";
      form.querySelectorAll("[data-pool-field]").forEach(function (n) {
        n.hidden = !inPool;
      });
      if (!inPool && wasPool && beforePool) {
        /* back from pool mode: the batch the visitor had before */
        f.target.value = beforePool.target;
        f.volume.value = beforePool.volume;
        f.unit.value = beforePool.unit;
        beforePool = null;
      }
      if (inPool && !wasPool) {
        beforePool = {
          target: f.target.value,
          volume: f.volume.value,
          unit: f.unit.value,
        };
        /* sensible starting point when someone switches to pool mode */
        if (!(Number(f.target.value) >= 7 && Number(f.target.value) <= 8.4))
          f.target.value = "7.5";
        if (!(Number(f.volume.value) >= 500)) f.volume.value = "15000";
        if (f.unit.value !== "gal" && f.unit.value !== "L")
          f.unit.value = "gal";
      }
      wasPool = inPool;
      var vl = form.querySelector("[data-volume-label]");
      if (vl) vl.textContent = inPool ? "Pool size" : "Batch size";
      f.stock.placeholder =
        !chem || chem.basis === "solid"
          ? ""
          : chem.needsStock
            ? "from label"
            : String(chem.stock);
      f.stock.disabled = !chem || chem.basis === "solid";
      var sf = form.querySelector("[data-stock-field]");
      if (sf) sf.hidden = !!(chem && chem.basis === "solid");
      var m = mode();
      out.targetLabel.textContent =
        m === "pool"
          ? "pH you want"
          : m === "ratio"
            ? "Parts water per part"
            : m === "ppm"
              ? chem && chem.species === "H2O2"
                ? "Peroxide you need"
                : "Chlorine you need"
              : "Strength you need";
      out.suffix.textContent =
        m === "pool"
          ? "pH"
          : m === "ratio"
            ? "parts"
            : m === "ppm"
              ? "ppm"
              : "%";
    }

    function li(list, text) {
      list.appendChild(el("li", null, text));
    }
    function fig(label, value) {
      var w = el("div");
      w.appendChild(el("dt", null, label));
      w.appendChild(el("dd", null, value));
      out.figures.appendChild(w);
    }

    var lastKey = "";
    function update(pushState) {
      var chem = chemNow();
      syncControls(chem);
      var unit = f.unit.value;
      var r = solve({
        chemical: chem || f.chemical.value,
        stock: f.stock.value,
        mode: mode(),
        target: f.target.value,
        volume: f.volume.value,
        unit: unit,
        phNow: f.phNow && f.phNow.value,
        ta: f.ta && f.ta.value,
        cya: f.cya && f.cya.value,
      });
      if (missing) {
        r = err("unknown-chemical", missing);
        missing = "";
      }
      out.steps.innerHTML = "";
      out.figures.innerHTML = "";
      out.notes.innerHTML = "";
      if (!r.ok) {
        out.msg.textContent = r.message;
        out.msg.hidden = false;
        out.result.hidden = true;
        renderBuy(null);
        return;
      }
      out.msg.hidden = true;
      out.result.hidden = false;
      var name = labelFor(chem, r.stock),
        total = formatVolume(r.total.L, unit);
      var strength =
        r.mode === "ppm"
          ? trim(r.target, 2) +
            (chem.species === "H2O2" ? " ppm H₂O₂" : " ppm available chlorine")
          : r.mode === "ratio"
            ? "1:" + trim(r.target, 3)
            : trim(r.target, 3) + "%";
      if (r.mode === "pool") {
        var dose = formatVolume(
          r.conc.L,
          unit === "L" || unit === "mL" ? "L" : "gal",
        );
        var half = formatVolume(
          r.conc.L / 2,
          unit === "L" || unit === "mL" ? "L" : "gal",
        );
        out.headline.textContent =
          "Add " +
          dose +
          " of " +
          name +
          " to lower pH from " +
          trim(r.phNow, 2) +
          " to " +
          trim(r.target, 2) +
          " in " +
          total +
          ".";
        if (r.split) {
          li(
            out.steps,
            "With the pump running, pour about half (" +
              half +
              ") slowly in front of a return jet in the deep end.",
          );
          li(
            out.steps,
            "Wait 30 minutes, retest pH, then add the rest only if pH is still above " +
              trim(r.target, 2) +
              ".",
          );
        } else {
          li(
            out.steps,
            "With the pump running, pour " +
              dose +
              " slowly in front of a return jet in the deep end.",
          );
          li(
            out.steps,
            "Wait 30 minutes, then retest pH and total alkalinity.",
          );
        }
        fig(
          "Muriatic acid",
          dose +
            (unit === "L" || unit === "mL"
              ? ""
              : " · " + formatVolume(r.conc.L, "L")),
        );
        fig(
          "Total alkalinity after",
          "about " +
            trim(Math.max(0, Number(f.ta.value) - r.taDrop), 0) +
            " ppm",
        );
        if (Number(f.cya.value) > 0)
          fig("Carbonate alkalinity now", trim(r.carbAlk, 0) + " ppm");
        out.keyConc.textContent = "Acid";
      } else if (r.basis === "solid") {
        var mass = formatMass(r.conc.g, unit);
        out.headline.textContent =
          "Dissolve " +
          mass +
          " of " +
          name +
          " to make " +
          total +
          " at " +
          strength +
          ".";
        li(
          out.steps,
          "Weigh " +
            mass +
            " of " +
            name +
            " as sold" +
            (unit === "gal" || unit === "qt" || unit === "floz"
              ? " (" + formatMass(r.conc.g, "L") + ")"
              : "") +
            ".",
        );
        li(
          out.steps,
          "Stir it into about " +
            formatVolume(r.total.L * 0.75, unit) +
            " of warm water until it dissolves.",
        );
        li(out.steps, "Add water to make " + total + ", then stir.");
        fig("Crystals", mass);
        fig("Finished batch", total);
        if (r.finalWw != null && r.mode !== "ppm")
          fig("Strength", trim(r.finalWw, 2) + "% w/v");
        out.keyConc.textContent = "Crystals";
      } else {
        var conc = formatVolume(r.conc.L, unit),
          water = formatVolume(r.water.L, unit);
        out.headline.textContent =
          r.mode === "ppm"
            ? "Add " +
              conc +
              " of " +
              name +
              " to " +
              total +
              " of water for " +
              strength +
              "."
            : "Mix " +
              conc +
              " of " +
              name +
              " with water to make " +
              total +
              " at " +
              strength +
              ".";
        if (r.mode === "ppm") {
          li(
            out.steps,
            "Add " +
              conc +
              " of " +
              name +
              " to " +
              total +
              " of water and mix well.",
          );
          li(
            out.steps,
            "Wait a few minutes, then check the level with a test kit.",
          );
        } else if (r.basis === "vv" && r.mode === "percent") {
          li(out.steps, "Measure " + conc + " of " + name + ".");
          li(
            out.steps,
            "Add water until the batch reaches " + total + ", then stir.",
          );
        } else {
          li(
            out.steps,
            "Put about " +
              water +
              " of water in a container that holds at least " +
              total +
              ".",
          );
          li(out.steps, "Slowly add " + conc + " of " + name + ", stirring.");
          if (r.topUp)
            li(out.steps, "Top up with water to " + total + " and stir.");
        }
        fig(
          "Concentrate",
          conc + (isMetric(unit) ? "" : " · " + formatVolume(r.conc.L, "L")),
        );
        if (r.mode !== "ppm")
          fig("Concentrate weight", formatMass(r.conc.g, unit));
        if (r.mode !== "ppm") fig("Water, about", water);
        if (r.mode === "ratio" && r.finalWw != null)
          fig("Strength", "about " + trim(r.finalWw, 2) + "% by weight");
        if (r.mode === "ratio" && r.finalVv != null)
          fig("Strength", "about " + trim(r.finalVv, 2) + "% by volume");
        if (r.mode === "percent")
          fig(
            "Strength",
            trim(r.target, 3) +
              (r.basis === "vv" ? "% by volume" : "% by weight"),
          );
        out.keyConc.textContent = "Concentrate";
      }
      var share =
        r.basis === "solid" || r.mode === "pool"
          ? 0.04
          : Math.max(0.02, Math.min(1, r.conc.L / r.total.L));
      out.tank.style.setProperty("--share", share.toFixed(4));
      if (out.basis) out.basis.textContent = basisNote(r);
      out.hazard.textContent = r.hazard;
      out.hazard.hidden = false;
      r.notes.forEach(function (n) {
        li(out.notes, n);
      });

      var calc = new URLSearchParams();
      calc.set("chemical", chem.id);
      if (f.stock.value) calc.set("stock", f.stock.value);
      calc.set("mode", r.mode);
      calc.set("target", f.target.value);
      calc.set("volume", f.volume.value);
      calc.set("unit", unit);
      if (r.mode === "pool") {
        calc.set("phNow", f.phNow.value);
        calc.set("ta", f.ta.value);
        calc.set("cya", f.cya.value);
      }
      var key = calc.toString();
      if (pushState !== false && key !== lastKey) {
        if (!isEmbed) {
          /* keep the URL's other parameters (view, utm_*) and its hash */
          var q = new URLSearchParams(location.search);
          CALC_KEYS.forEach(function (k) {
            q.delete(k);
          });
          calc.forEach(function (v, k) {
            q.set(k, v);
          });
          try {
            history.replaceState(
              null,
              "",
              location.pathname + "?" + q.toString() + location.hash,
            );
          } catch (e) {
            /* Safari throttles replaceState; the next edit tries again */
          }
        }
        clearTimeout(gaTimer);
        gaTimer = setTimeout(function () {
          ga("dilution_calculate", { chemical: chem.id, mode: r.mode });
        }, 1000);
      }
      lastKey = key;
      renderBuy(r);
    }

    var buyToken = 0;
    function renderBuy(r) {
      if (!buy.root) return;
      var token = ++buyToken;
      buy.alts.innerHTML = "";
      if (buy.cart) {
        buy.cart.hidden = true;
        buy.cart.querySelector("[data-cart-msg]").hidden = true;
        buy.cart.querySelector("[data-cart-label]").textContent = "Add to cart";
      }
      if (!r) {
        buy.body.innerHTML = "";
        return;
      }
      var chem = r.chem;
      if (!chem.buy) {
        buy.body.innerHTML = "";
        buy.body.appendChild(
          el(
            "p",
            "ac-dilute__buy-title",
            chem.id === "other"
              ? "Not on the list? We stock 340+ chemicals."
              : "We don't stock " + chem.name + ".",
          ),
        );
        var a = el("a", "ac-ind-link", "Search our catalog");
        a.href =
          chem.id === "other"
            ? root + "search"
            : root +
              "search?q=" +
              encodeURIComponent(chem.name.replace(/\s*\(.*$/, ""));
        buy.body.appendChild(a);
        return;
      }
      /* the visitor may have typed their own strength; the card sells ours, so size it for ours */
      var own = r.basis !== "solid" && Math.abs(r.stock - chem.stock) > 1e-9;
      var forOurs = own
        ? solve({
            chemical: chem,
            mode: r.mode,
            target: r.target,
            volume: r.total.L,
            unit: "L",
          })
        : r;
      if (!forOurs.ok) forOurs = r;
      var needUnit = r.basis === "solid" ? "lb" : "gal";
      var need =
        r.basis === "solid" ? forOurs.conc.g / LB_G : forOurs.conc.L / GAL_L;
      getProduct(chem.buy, form).then(function (p) {
        if (token !== buyToken) return;
        buy.body.innerHTML = "";
        if (!p) {
          var fail = el("p", null, "Prices didn't load. ");
          var see0 = el("a", "ac-ind-link", "See sizes and prices");
          see0.href = root + "products/" + chem.buy;
          fail.appendChild(see0);
          buy.body.appendChild(fail);
          return;
        }
        var title = el("p", "ac-dilute__buy-title");
        var link = el("a", null, p.title);
        link.href = p.url;
        title.appendChild(link);
        buy.body.appendChild(title);
        var pick = choosePack(need, needUnit, p.variants);
        var needText =
          needUnit === "lb"
            ? formatMass(forOurs.conc.g, "gal")
            : formatVolume(forOurs.conc.L, "gal");
        buy.body.appendChild(
          el(
            "p",
            "ac-dilute__buy-need",
            (own && forOurs !== r
              ? "With our " + trim(chem.stock, 2) + "%, this batch uses "
              : "This batch uses ") +
              needText +
              ".",
          ),
        );
        if (!pick || !pick.best) {
          var none = el(
            "p",
            "ac-dilute__buy-big",
            pick
              ? "That is more than we ship in one online order. "
              : "No size is available to order online right now. ",
          );
          var next = el(
            "a",
            "ac-ind-link",
            pick ? "Get a bulk quote" : "See sizes",
          );
          next.href = pick ? "/pages/request-a-quote" : p.url;
          none.appendChild(next);
          buy.body.appendChild(none);
          return;
        }
        /* size picker: every pack that covers this batch, best fit pre-selected */
        var opts = packOptions(need, needUnit, p.variants);
        if (opts.length && buy.cart) {
          var fs = el("fieldset", "ac-dilute__sizes");
          fs.appendChild(el("legend", null, "Pick a size"));
          var group =
            "ac-dilute-size-" +
            (form.id ||
              (form.id =
                "ac-dilute-" + Math.random().toString(36).slice(2, 8)));
          var setCart = function (o) {
            buy.cart.querySelector("[data-cart-id]").value = o.variant.id;
            buy.cart.querySelector("[data-cart-qty]").value = o.count;
            buy.cart.querySelector("[data-cart-label]").textContent =
              "Add to cart · " + money(o.cost);
          };
          opts.forEach(function (o) {
            var row = el(
              "label",
              "ac-dilute__size" + (o.best ? " is-best" : ""),
            );
            var input = document.createElement("input");
            input.type = "radio";
            input.name = group;
            input.checked = o.best;
            input.addEventListener("change", function () {
              setCart(o);
            });
            row.appendChild(input);
            var nameEl = el(
              "span",
              "ac-dilute__size-name",
              (o.count > 1 ? o.count + " × " : "") + o.variant.title,
            );
            if (o.best)
              nameEl.appendChild(
                el("span", "ac-dilute__size-badge", "Fits this batch"),
              );
            row.appendChild(nameEl);
            row.appendChild(el("span", "ac-dilute__size-price", money(o.cost)));
            row.appendChild(
              el(
                "span",
                "ac-dilute__size-per",
                money(o.perUnit) + "/" + needUnit,
              ),
            );
            fs.appendChild(row);
          });
          buy.body.appendChild(fs);
          setCart(
            opts.filter(function (o) {
              return o.best;
            })[0] || opts[0],
          );
          buy.cart.hidden = false;
        }
        if (
          !opts.length ||
          pick.quote ||
          opts[opts.length - 1].total < CART_MAX[needUnit]
        ) {
          var big = el("p", "ac-dilute__buy-big");
          var see = el(
            "a",
            "ac-ind-link",
            opts.length ? "Drums and totes" : "See drum and tote sizes",
          );
          see.href = p.url;
          big.appendChild(see);
          big.appendChild(document.createTextNode(" · "));
          var qa = el("a", "ac-ind-link", "Bulk quote");
          qa.href = "/pages/request-a-quote";
          big.appendChild(qa);
          buy.body.appendChild(big);
        }
        var ready = r.mode === "percent" ? readyMade(chem.id, r.target) : null;
        if (ready) {
          getProduct(ready, form).then(function (rp) {
            if (!rp || token !== buyToken) return;
            var prices = rp.variants
              .filter(function (x) {
                return x.available;
              })
              .map(function (x) {
                return x.price;
              });
            var w = el("p", "ac-dilute__alt");
            w.appendChild(
              el("span", "ac-dilute__alt-label", "Skip the mixing"),
            );
            var ra = el("a", null, rp.title);
            ra.href = rp.url;
            w.appendChild(ra);
            if (prices.length)
              w.appendChild(
                document.createTextNode(
                  " from " + money(Math.min.apply(null, prices)),
                ),
              );
            buy.alts.appendChild(w);
          });
        }
      });
    }

    if (buy.cart) {
      buy.cart.addEventListener("submit", function (e) {
        e.preventDefault();
        flushEdit();
        var btn = buy.cart.querySelector("button");
        var label = buy.cart.querySelector("[data-cart-label]");
        var msg = buy.cart.querySelector("[data-cart-msg]");
        if (btn.getAttribute("aria-busy") === "true") return;
        var id = parseInt(buy.cart.querySelector("[data-cart-id]").value, 10);
        var startToken = buyToken;
        var chemAtAdd = f.chemical.value;
        var qty =
          parseInt(buy.cart.querySelector("[data-cart-qty]").value, 10) || 1;
        btn.setAttribute("aria-busy", "true");
        label.textContent = "Adding…";
        msg.hidden = true;
        fetch(root + "cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ items: [{ id: id, quantity: qty }] }),
        })
          .then(function (r) {
            return r
              .json()
              .catch(function () {
                return {};
              })
              .then(function (body) {
                if (!r.ok)
                  throw new Error(
                    body.description ||
                      body.message ||
                      "Couldn't add that to the cart. Please try again.",
                  );
                return body;
              });
          })
          .then(function () {
            return fetch(root + "cart/update.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({
                attributes: { _source: "dilution-calculator" },
              }),
            }).catch(function () {});
          })
          .then(function () {
            ga("dilution_add_to_cart", {
              variant_id: id,
              quantity: qty,
              chemical: chemAtAdd,
            });
            if (startToken === buyToken) {
              /* the card still shows the pack that was added */
              label.textContent = "Added";
              msg.innerHTML =
                'In your cart. <a href="' + root + 'cart">View cart</a>';
              msg.hidden = false;
            }
            return refreshHeader().catch(function () {});
          })
          .catch(function (e2) {
            if (startToken !== buyToken) return;
            label.textContent = "Add to cart";
            msg.textContent =
              e2 instanceof TypeError
                ? "Network problem. Please try again."
                : e2.message;
            msg.hidden = false;
          })
          .then(function () {
            btn.removeAttribute("aria-busy");
          });
      });
    }

    /* Typing paints first and the answer follows in the next task: update()
       rebuilds the result and the Mix & Buy card (~160 ms on a mid phone),
       which inside the input event was the page's INP. One pending update
       covers a burst of keys and the input + change pair a select fires. */
    var editPending = false;
    function flushEdit() {
      if (!editPending) return;
      editPending = false;
      update();
    }
    function onEdit(e) {
      if (e.target === f.chemical) f.stock.value = "";
      /* A typed field's blur fires "change" in the same tap as the click that
         blurred it; its input events already scheduled the update, and a
         re-render landing after that click would rebuild the buy card under
         Add to cart or a size the visitor just picked. */
      var t = e.target;
      if (
        e.type === "change" &&
        t.tagName === "INPUT" &&
        t.type !== "radio" &&
        t.type !== "checkbox"
      )
        return;
      if (editPending) return;
      editPending = true;
      requestAnimationFrame(function () {
        setTimeout(flushEdit, 0);
      });
    }
    form.addEventListener("input", onEdit);
    form.addEventListener("change", onEdit);
    form.addEventListener("submit", function (e) {
      e.preventDefault();
      editPending = false;
      update();
    });
    form.__acDiluteLoad = function (params) {
      editPending = false;
      f.stock.value = "";
      apply(params);
      update();
    };
    form.querySelectorAll("[data-preset]").forEach(function (a) {
      a.addEventListener("click", function (e) {
        e.preventDefault();
        editPending = false;
        f.stock.value = "";
        apply(
          new URLSearchParams(a.getAttribute("data-preset").replace(/^\?/, "")),
        );
        update();
        f.target.focus({ preventScroll: true });
      });
    });
    var copy = form.querySelector("[data-copy]");
    if (copy)
      copy.addEventListener("click", function () {
        flushEdit();
        var done = function () {
          copy.textContent = "Link copied";
          setTimeout(function () {
            copy.textContent = "Copy link to this recipe";
          }, 2000);
        };
        var url = isEmbed
          ? location.origin + root + "pages/dilution?" + lastKey
          : location.href;
        if (navigator.clipboard)
          navigator.clipboard.writeText(url).then(done, done);
        else window.prompt("Copy this link", url);
      });
    var pr = form.querySelector("[data-print]");
    if (pr)
      pr.addEventListener("click", function () {
        window.print();
      });

    apply(params || new URLSearchParams(location.search), true);
    update(false);
  }

  /* article embed: <div data-ac-dilute-embed data-chemical="..." data-target="..." data-volume="..." data-unit="..."> */
  function embed(host) {
    var tpl = document.getElementById("ac-dilute-embed-template");
    if (host.__acDilute || !tpl) return;
    host.__acDilute = true;
    var wrap = el("div", "ac-dilute ac-dilute--embed");
    wrap.setAttribute("data-ac-dilute", "");
    wrap.appendChild(tpl.content.cloneNode(true));
    /* every embed on a page needs its own ids */
    var n = (embed.count = (embed.count || 0) + 1);
    wrap.querySelectorAll("[id]").forEach(function (x) {
      x.id += "-" + n;
    });
    wrap.querySelectorAll("[for], [aria-labelledby]").forEach(function (x) {
      ["for", "aria-labelledby"].forEach(function (a) {
        if (x.getAttribute(a)) x.setAttribute(a, x.getAttribute(a) + "-" + n);
      });
    });
    var params = new URLSearchParams();
    /* data-ph-now / data-ta / data-cya carry pool readings (HTML attribute names are lower-case) */
    [
      ["chemical", "chemical"],
      ["mode", "mode"],
      ["target", "target"],
      ["volume", "volume"],
      ["unit", "unit"],
      ["ph-now", "phNow"],
      ["ta", "ta"],
      ["cya", "cya"],
    ].forEach(function (k) {
      if (host.getAttribute("data-" + k[0]))
        params.set(k[1], host.getAttribute("data-" + k[0]));
    });
    host.innerHTML = "";
    host.appendChild(wrap);
    mount(wrap.querySelector("[data-ac-dilute-form]"), params);
  }

  /* Guide tables: label each cell for the phone card layout; recipe links that point at this page load the
     recipe into the calculator in place (no reload) and scroll to it. */
  function enhanceGuide(scope) {
    scope.querySelectorAll(".ac-dilute-guide table").forEach(function (t) {
      if (t.__acDilute) return;
      t.__acDilute = true;
      var heads = Array.prototype.map.call(
        t.querySelectorAll("thead th"),
        function (th) {
          return th.textContent.trim();
        },
      );
      t.querySelectorAll("tbody tr").forEach(function (tr) {
        Array.prototype.forEach.call(tr.children, function (td, i) {
          if (heads[i]) td.setAttribute("data-label", heads[i]);
          var a = td.querySelector('a[href*="?chemical="]');
          if (a) {
            a.textContent = "Calculate";
            a.classList.add("ac-dilute__calc-link");
            td.classList.add("ac-dilute__calc-cell");
          }
        });
      });
    });
    scope
      .querySelectorAll('.ac-dilute-guide a[href*="?chemical="]')
      .forEach(function (a) {
        if (a.__acDilute) return;
        a.__acDilute = true;
        a.addEventListener("click", function (e) {
          var url = new URL(a.href, location.href);
          var form = document.querySelector(
            "[data-ac-dilute-form]:not([data-embed])",
          );
          if (
            url.pathname !== location.pathname ||
            !form ||
            !form.__acDiluteLoad
          )
            return;
          e.preventDefault();
          form.__acDiluteLoad(url.searchParams);
          form.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      });
  }

  function init(scope) {
    enhanceGuide(scope);
    scope.querySelectorAll("[data-ac-dilute-form]").forEach(function (form) {
      mount(form);
    });
    scope.querySelectorAll("[data-ac-dilute-embed]").forEach(embed);
  }
  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", function () {
      init(document);
    });
  else init(document);
  document.addEventListener("shopify:section:load", function (e) {
    init(e.target);
  });
})();
